import React, { useState, useEffect } from "react";
import { InputText } from "primereact/inputtext";
import { InputMask } from "primereact/inputmask";
import { Button } from "primereact/button";
import { classNames } from "primereact/utils";
import { useNavigate } from "react-router-dom";
import TokenService from "../utils/TokenService";
import MainMenu from "../components/MainMenu";
import authApi from "../service/IdApi";
import { useAppContext } from "../context/appContext";
import loginApi from "../service/LoginApi";
import { throttle } from "lodash";

const Setup2FA = () => {
    const { displayAlert } = useAppContext();
    const navigate = useNavigate();
    const isRequired = TokenService.is2faSetupRequired();
    const isEncouraged = TokenService.is2faSetupEncouraged();

    const [existingMethods, setExistingMethods] = useState([]);
    const [isLoadingMethods, setIsLoadingMethods] = useState(true);
    const [methodType, setMethodType] = useState("sms");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [codeSent, setCodeSent] = useState(false);
    const [code, setCode] = useState("");
    const [isSendingCode, setIsSendingCode] = useState(false);
    const [isVerifying, setIsVerifying] = useState(false);
    const [invalidCode, setInvalidCode] = useState(false);
    const [resendCooldown, setResendCooldown] = useState(0);
    const [mobileTopbarActive] = useState(false);

    useEffect(() => {
        if (!TokenService.isAuthenticated()) {
            TokenService.setRedirectOnLogin(window.location.href);
            navigate("/");
            return;
        }
        if (TokenService.isLoggedIn() && !isRequired && !isEncouraged) {
            /* TODO: Profile-complete (upc) requirement is temporarily disabled.
            if (!TokenService.isUserProfileComplete()) {
                navigate("/complete-profile");
            } else {
                navigate("/account/select-provider");
            }
            */
            navigate("/account/select-provider");
            return;
        }
        authApi.get("auth/2fa/methods")
            .then((response) => {
                const methods = response.data?.methods || [];
                if (methods.length > 0) {
                    navigate("/verify-2fa");
                    return;
                }
                setExistingMethods(methods);
            })
            .catch(() => setExistingMethods([]))
            .finally(() => setIsLoadingMethods(false));
    }, [navigate, isRequired, isEncouraged]);

    useEffect(() => {
        if (resendCooldown <= 0) return;
        const timer = setInterval(() => setResendCooldown((prev) => (prev <= 1 ? 0 : prev - 1)), 1000);
        return () => clearInterval(timer);
    }, [resendCooldown]);

    const phoneDigits = (phoneNumber || "").replace(/\D/g, "");
    const phoneValid = phoneDigits.length >= 10;
    const normalizedPhone = phoneDigits.length >= 10 ? "+1" + phoneDigits.slice(-10) : null;

    const handleSendCode = async (e, isResend = false) => {
        e?.preventDefault();
        if (!normalizedPhone || isSendingCode) return;
        try {
            setIsSendingCode(true);
            if (!isResend) {
                await authApi.post("auth/2fa/start-config", {
                    method: methodType,
                    phoneNumber: normalizedPhone,
                });
            }
            await authApi.post("auth/2fa/send-code", {
                method: methodType,
                phoneNumber: normalizedPhone,
            });
            if (!isResend) setCodeSent(true);
            setResendCooldown(60);
            displayAlert("success", "Success", "Verification code sent");
        } catch (err) {
            const msg = err?.response?.data?.msg || err?.response?.data?.message || err?.message || "";
            displayAlert("error", "Error", msg || "Failed to send code. Please try again.");
        } finally {
            setIsSendingCode(false);
        }
    };

    const handleVerifyAndEnable = async (e) => {
        e?.preventDefault();
        setInvalidCode(false);
        if (!code || code.trim().length !== 6) {
            displayAlert("error", "Error", "Please enter the 6-digit verification code");
            setInvalidCode(true);
            return;
        }
        try {
            setIsVerifying(true);
            const response = await authApi.post("auth/2fa/verify-config", {
                code: code.trim(),
                method: methodType,
                loginIfVerified: true,
            });
            const data = response.data;
            TokenService.setAccessToken(data.userAccessToken);
            TokenService.setRefreshToken(data.userRefreshToken);
            displayAlert("success", "Success", "Two-factor authentication is now enabled.");
            setTimeout(() => {
                // Give the browser a split second to update the cookies just in case
                /* TODO: Profile-complete (upc) requirement is temporarily disabled.
                if (!TokenService.isUserProfileComplete()) {
                    navigate("/complete-profile");
                } else {
                    TokenService.redirectUser(data.providerCount);
                }
                */
                TokenService.redirectUser(data.providerCount);
            }, 100);
        } catch (err) {
            setIsVerifying(false);
            const msg = err?.response?.data?.msg || err?.response?.data?.message || "";
            setInvalidCode(true);
            displayAlert("error", "Error", msg.includes("expired") ? "Code expired. Request a new one." : msg || "Invalid code. Please try again.");
        }
    };

    const layoutContainerClassName = classNames(
        "layout-wrapper",
        "pages-body",
        "login-page",
        "flex",
        "flex-column",
        "layout-menu-cyan layout-topbar-cyan",
        { "layout-topbar-mobile-active": mobileTopbarActive }
    );

    if (!TokenService.isAuthenticated()) {
        return null;
    }

    const backToLoginClicked = throttle(async () => {
        try {
            await loginApi.get("auth/logout");
            TokenService.logoutUser();
        } catch (error) {
            TokenService.logoutUser();
        }
        navigate("/login");
    }, 500, { trailing: false });

    return (
        <div className={layoutContainerClassName}>
            <MainMenu />
            <div className="align-self-center mt-auto mb-auto w-full" style={{ maxWidth: "500px" }}>
                <div className="pages-panel card flex flex-column py-4 px-1 sm:px-4 text-left">
                    <div className="pages-header mb-3 px-3 py-2">
                        <h2>Account Verification</h2>
                    </div>

                    {isLoadingMethods ? (
                        <div className="px-2 pb-4 text-center">Loading...</div>
                    ) : (
                        <>
                            {!codeSent ? (
                                <>
                                    <div className="pages-detail mb-4 px-2">
                                        <p className="m-0 text-lg text-left">
                                            {isRequired && "Your account requires two-factor authentication to be enabled. Please add a verification method below."}
                                            {isEncouraged && !isRequired && (
                                                <>
                                                    <span>To protect your account and company information, we recommend enabling two-factor authentication. Please select a verification method below</span> or <a href="/account/select-provider">skip this for now</a>.
                                                </>
                                            )}
                                        </p>
                                    </div>

                                    <form className="form" onSubmit={handleSendCode}>
                                        <div className="flex flex-column gap-3 px-2 pb-3">
                                            <div className="field">
                                                <label htmlFor="phone" className="block mb-2 font-bold">Phone number (US)</label>
                                                <InputMask
                                                    id="phone"
                                                    mask="(999) 999-9999"
                                                    name="phone"
                                                    value={phoneNumber}
                                                    onChange={(e) => setPhoneNumber(e.target.value || "")}
                                                    unmask
                                                    className="w-full"
                                                    autoFocus
                                                />
                                                <div className="text-700 text-sm mt-1">We'll send a 6-digit verification code to this number</div>
                                            </div>
                                            <div className="field">
                                                <label className="block mb-2 font-bold">Verification method</label>
                                                <div className="flex flex-column gap-2">
                                                    <label className="flex align-items-center cursor-pointer">
                                                        <input type="radio" name="methodType" value="sms" checked={methodType === "sms"} onChange={() => setMethodType("sms")} className="mr-2 mb-1" />
                                                        Text Message (SMS)
                                                    </label>
                                                    <label className="flex align-items-center cursor-pointer">
                                                        <input type="radio" name="methodType" value="voice" checked={methodType === "voice"} onChange={() => setMethodType("voice")} className="mr-2" />
                                                        Voice Call
                                                    </label>
                                                </div>
                                            </div>
                                            <div className="flex flex-row justify-content-between">
                                                <div>
                                                    <Button type="button" label="Back to login" icon="pi pi-arrow-left" className="p-button-text text-sm sm:text-base" onClick={backToLoginClicked} />
                                                </div>
                                                <div>
                                                    <Button
                                                        type="submit"
                                                        label="Send verification code"
                                                        icon="pi pi-send"
                                                        disabled={!phoneValid || isSendingCode}
                                                        loading={isSendingCode}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </form>
                                </>
                            ) : (
                                <form className="form" onSubmit={handleVerifyAndEnable}>
                                    <div className="flex flex-column gap-3 px-2 pt-2">
                                        <p className="m-0 mb-3 text-lg">Enter the 6-digit code sent to your phone.</p>
                                        <div>
                                            <span className="p-float-label">
                                                <InputText
                                                    id="code"
                                                    type="tel"
                                                    value={code}
                                                    onChange={(e) => {
                                                        setCode(e.target.value.replace(/\D/g, "").slice(0, 6));
                                                        setInvalidCode(false);
                                                    }}
                                                    className={classNames("w-full", { "p-invalid": invalidCode })}
                                                    placeholder="000000"
                                                    maxLength={6}
                                                    autoFocus
                                                    inputMode="numeric"
                                                    style={{ fontSize: "1.5rem", letterSpacing: "0.5rem", textAlign: "center", fontFamily: "monospace" }}
                                                />
                                                <label htmlFor="code">Verification Code</label>
                                            </span>
                                        </div>
                                        {resendCooldown > 0 ? (
                                            <p className="text-700 text-sm m-0 text-center">Request a new code in {resendCooldown}s</p>
                                        ) : (
                                            <div className="text-center">
                                                <Button type="button" label="Resend code" icon="pi pi-refresh" className="p-button-text" onClick={(e) => handleSendCode(e, true)} disabled={isSendingCode} />
                                            </div>
                                        )}
                                        <div className="flex flex-row justify-content-between">
                                            <div>
                                                <Button type="button" label="Back to login" icon="pi pi-arrow-left" className="p-button-text text-sm sm:text-base" onClick={backToLoginClicked} />
                                            </div>
                                            <div>
                                                <Button
                                                    type="submit"
                                                    label="Verify & Continue"
                                                    icon="pi pi-check"
                                                    disabled={!code || code.trim().length !== 6 || isVerifying}
                                                    loading={isVerifying}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </form>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default Setup2FA;
